<script setup lang="ts">
import { computed, onUnmounted, reactive, ref, useTemplateRef, watch } from 'vue';
import { useEventListener } from '@vueuse/core';
import { DateTime } from 'luxon';
import { uniqueId } from 'lodash-es';
import DateRangePicker from 'vue3-daterange-picker';
import ContactApi, { type ContactRepresentativeOption } from '@/modules/counterparty/api/contact-api.ts';
import type {
  CounterpartyContactFormData,
  CounterpartyContactItem,
} from '@/modules/counterparty/counterparty-master/types/contact-form.ts';
import { isPersonalContact } from '@/modules/counterparty/counterparty-master/types/contact-form.ts';
import {
  BIRTH_DAY_FORMAT,
  BIRTH_DAY_MASK,
  displayDateToSql,
  parseBirthDay,
  resolvePartialBirthDay,
  sqlDateToDisplay,
} from '@/modules/counterparty/counterparty-master/utils/contact-birth-day.ts';
import { useAbort } from '@/modules/common/composables/use-abort.ts';
import { useDatePickerLocale } from '@/modules/common/composables/use-date-picker-locale.ts';
import { COMMENT_MAX_LENGTH } from '@/modules/common/consts/max-length.ts';
import { IdPrefix } from '@/modules/common/enums/id-prefix.ts';
import type { PickerDateRange } from '@/modules/common/types/luxon.type.ts';
import type { Option } from '@/modules/common/types/option.type.ts';
import { ValueUtil } from '@/modules/common/utils/value.util.ts';
import Validator from '@/modules/common/utils/validator.ts';
import { getInputMaskMaxLength, toInputMask } from '@/modules/common/utils/to-input-mask.ts';
import { useFormTooltips } from '@/modules/ui/composables/use-form-tooltips.ts';
import AddButton from '@/modules/core/components/buttons/AddButton.vue';
import CloseButton from '@/modules/ui/components/buttons/CloseButton.vue';
import SaveButton from '@/modules/ui/components/buttons/SaveButton.vue';
import ModalForm from '@/modules/ui/components/common/ModalForm.vue';
import UiCombobox from '@/modules/ui/components/ui/UiCombobox.vue';
import UiField from '@/modules/ui/components/ui/UiField.vue';
import UiInput from '@/modules/ui/components/ui/UiInput.vue';
import UiTextarea from '@/modules/ui/components/ui/UiTextarea.vue';
import UiToggle from '@/modules/ui/components/ui/UiToggle.vue';
import UiButton from '@/modules/ui/components/ui/UiButton.vue';
import CircleCloseIcon from '@/assets/icons/shared/circle/close.svg';

const PH = {
  type: 'counterparty.ui.common.label.enterType',
  contact: 'counterparty.ui.common.placeholder.enterContact',
  fio: 'counterparty.master.steps.contact.field.fio',
  birthDay: 'counterparty.master.steps.contact.field.birthDay',
  birthDayFormat: 'counterparty.master.steps.contact.field.birthDayFormat',
  position: 'counterparty.master.steps.contact.field.position',
  typeToAdd: 'counterparty.master.steps.contact.field.typeToAdd',
  representative: 'counterparty.master.steps.contact.field.representative',
  clear: 'counterparty.master.steps.contact.field.clear',
  comment: 'ui.common.placeholder.enterNote',
} as const;

const isOpen = defineModel<boolean>({ required: true });

const props = defineProps<{
  editItem?: CounterpartyContactItem | null;
  positionOptions?: CounterpartyContactItem[];
  loading?: boolean;
  title?: string;
}>();

const emit = defineEmits<{
  submit: [value: CounterpartyContactFormData];
}>();

const { signal, abort } = useAbort();
const type = ref<Option | null>(null);
const contact = ref('');
const isPersonal = ref(false);
const fioOption = ref<ContactRepresentativeOption | Option | null>(null);
const birthDay = ref('');
const position = ref<Option | null>(null);
const comment = ref('');
const showErrors = ref(false);
const masksByType = ref(new Map<string, string>());
const { tooltipsDisabled, onInput } = useFormTooltips();

const fieldIds = {
  type: uniqueId(IdPrefix.Control),
  contact: uniqueId(IdPrefix.Control),
  personal: uniqueId(IdPrefix.Control),
  fio: uniqueId(IdPrefix.Control),
  birthDay: uniqueId(IdPrefix.Control),
  position: uniqueId(IdPrefix.Control),
  comment: uniqueId(IdPrefix.Control),
};

const isEdit = computed(() => !!props.editItem);

const title = computed(
  () =>
    props.title ??
    (isEdit.value
      ? 'counterparty.master.steps.contact.form.editTitle'
      : 'counterparty.master.steps.contact.form.addTitle')
);

const typeError = computed(() => Validator.required(type.value));
const contactError = computed(() => Validator.required(contact.value.trim()));
const localeData = useDatePickerLocale();
const birthDayMax = computed(() => DateTime.now().endOf('day').toJSDate());

const birthDayError = computed(() => {
  if (!isPersonal.value) {
    return undefined;
  }

  const digits = birthDay.value.replace(/\D/gu, '');

  if (!digits) {
    return undefined;
  }

  const date = parseBirthDay(birthDay.value);

  if (!date) {
    return { key: 'ui.field.error.enterValidDate', params: { format: 'DD.MM.YYYY' } };
  }

  if (date.startOf('day') > DateTime.now().startOf('day')) {
    return 'ui.field.error.dateMustBeLessOrEqualCurrent';
  }

  return undefined;
});

const birthDayRange = reactive<PickerDateRange>({
  startDate: null,
  endDate: null,
});

const birthDayPicker = useTemplateRef<{
  changeLeftMonth: (value: { month: number; year: number }) => void;
  togglePicker: (value: boolean | null, event?: boolean) => void;
}>('birthDayPicker');

const contactTypeKey = computed(() => type.value?.label.trim() || '');

const contactMaskSource = computed(() => {
  if (!contactTypeKey.value) {
    return '';
  }

  const fromTable = masksByType.value.get(contactTypeKey.value);

  if (fromTable) {
    return fromTable;
  }

  const editMask = props.editItem?.mask?.trim();
  const editType = props.editItem?.type.label.trim() || '';

  if (editMask && editType === contactTypeKey.value) {
    return editMask;
  }

  return '';
});

const contactInputMask = computed(() => toInputMask(contactMaskSource.value || undefined));

const contactInputKey = computed(() => `${contactTypeKey.value || 'contact'}:${contactMaskSource.value}`);

const contactMaxLength = computed(() => getInputMaskMaxLength(contactInputMask.value, COMMENT_MAX_LENGTH));

const fioLabel = computed(() => fioOption.value?.label.trim() ?? '');

const showRepresentativeClear = computed(
  () => isPersonal.value && (!!fioLabel.value || !!position.value || !!birthDay.value)
);

function setType(next: Option | null) {
  const prevLabel = type.value?.label.trim() || '';
  const nextLabel = next?.label.trim() || '';

  type.value = next;

  if (prevLabel && nextLabel && prevLabel !== nextLabel) {
    contact.value = '';
  }
}

const typeModel = computed({
  get: () => type.value,
  set: setType,
});

async function getTypeOptions(signal: AbortSignal): Promise<Option[]> {
  return ContactApi.getTypes(signal);
}

function normalizeOptionLabel(value: string) {
  return value.replace(/\s+/gu, ' ').trim().toLowerCase();
}

const fioOptions = computed(() => {
  const seen = new Set<string>();
  const options: ContactRepresentativeOption[] = [];

  for (const item of props.positionOptions ?? []) {
    const fio = item.fio.trim();
    const key = normalizeOptionLabel(fio);

    if (!fio || seen.has(key)) {
      continue;
    }

    seen.add(key);
    options.push({
      code: String(item.id),
      label: fio,
      position: item.position?.label.trim() ?? '',
      birthDay: item.birthDay,
    });
  }

  return options;
});

async function getPositionOptions(signal: AbortSignal): Promise<Option[]> {
  const remote = await ContactApi.searchPositions('', signal);
  const seen = new Set(remote.map((option) => normalizeOptionLabel(option.label)));
  const options = [...remote];

  for (const item of props.positionOptions ?? []) {
    const label = item.position?.label.trim();

    if (!label) {
      continue;
    }

    const key = normalizeOptionLabel(label);

    if (seen.has(key)) {
      continue;
    }

    seen.add(key);
    options.push({ code: label, label });
  }

  return options;
}

function syncBirthDayCalendarMonth(date: DateTime) {
  birthDayPicker.value?.changeLeftMonth({
    month: date.month,
    year: date.year,
  });
}

function syncBirthDayRange(value: string) {
  const date = resolvePartialBirthDay(value);
  const jsDate = date?.toJSDate() ?? null;

  birthDayRange.startDate = jsDate;
  birthDayRange.endDate = jsDate;

  if (date) {
    syncBirthDayCalendarMonth(date);
  }
}

function onBirthDayPickerChange(range: PickerDateRange) {
  if (!range.startDate) {
    return;
  }

  birthDay.value = DateTime.fromJSDate(range.startDate).toFormat(BIRTH_DAY_FORMAT);
  onInput();
}

let birthDayPickerMouseDown = false;
let birthDayBlurCloseTimeout: ReturnType<typeof setTimeout> | undefined;

function isBirthDayPickerTarget(target: EventTarget | null) {
  return target instanceof Element && !!target.closest('.daterangepicker');
}

useEventListener(
  document,
  'mousedown',
  (event: MouseEvent) => {
    birthDayPickerMouseDown = isOpen.value && isPersonal.value && isBirthDayPickerTarget(event.target);
  },
  { capture: true }
);

function onBirthDayClick() {
  if (props.loading) {
    return;
  }

  birthDayPicker.value?.togglePicker(true, true);

  const date = resolvePartialBirthDay(birthDay.value);

  if (date) {
    syncBirthDayCalendarMonth(date);
  }
}

function onBirthDayBlur() {
  if (birthDayBlurCloseTimeout !== undefined) {
    clearTimeout(birthDayBlurCloseTimeout);
  }

  birthDayBlurCloseTimeout = setTimeout(() => {
    birthDayBlurCloseTimeout = undefined;

    if (birthDayPickerMouseDown || isBirthDayPickerTarget(document.activeElement)) {
      birthDayPickerMouseDown = false;
      return;
    }

    birthDayPicker.value?.togglePicker(false, true);
  });
}

onUnmounted(() => {
  if (birthDayBlurCloseTimeout !== undefined) {
    clearTimeout(birthDayBlurCloseTimeout);
  }
});

watch(birthDay, syncBirthDayRange, { immediate: true, flush: 'post' });

function clearRepresentative() {
  fioOption.value = null;
  birthDay.value = '';
  position.value = null;
}

function isRepresentativeOption(option: ContactRepresentativeOption | Option): option is ContactRepresentativeOption {
  return 'position' in option && 'birthDay' in option;
}

function applyFioOption(option: ContactRepresentativeOption | Option | null) {
  if (!option) {
    clearRepresentative();
    return;
  }

  fioOption.value = option;

  if (!isRepresentativeOption(option)) {
    birthDay.value = '';
    position.value = null;
    return;
  }

  birthDay.value = sqlDateToDisplay(option.birthDay);
  position.value = option.position.trim() ? { code: option.position.trim(), label: option.position.trim() } : null;
}

const fioModel = computed({
  get: () => fioOption.value,
  set: applyFioOption,
});

function resetForm() {
  type.value = null;
  contact.value = '';
  isPersonal.value = false;
  clearRepresentative();
  comment.value = '';
  showErrors.value = false;
}

function applyFormFromEditItem() {
  if (!props.editItem) {
    resetForm();
    return;
  }

  type.value = { ...props.editItem.type };
  contact.value = props.editItem.contact;
  comment.value = props.editItem.comment;
  showErrors.value = false;

  const personal = isPersonalContact(props.editItem);
  isPersonal.value = personal;

  if (!personal) {
    clearRepresentative();
    return;
  }

  const fio = props.editItem.fio.trim();
  fioOption.value = fio ? { code: fio, label: fio } : null;
  birthDay.value = sqlDateToDisplay(props.editItem.birthDay);
  position.value = props.editItem.position ? { ...props.editItem.position } : null;
}

watch(isOpen, async (open) => {
  if (!open) {
    abort();
    return;
  }

  applyFormFromEditItem();
  abort();

  const requestSignal = signal.value;

  try {
    const masks = await ContactApi.getMasks(requestSignal);

    if (requestSignal.aborted || !isOpen.value) {
      return;
    }

    masksByType.value = masks;
  } catch {
    if (requestSignal.aborted || !isOpen.value) {
      return;
    }

    masksByType.value = new Map();
  }
});

function submit() {
  if (props.loading) {
    return;
  }

  showErrors.value = !!typeError.value || !!contactError.value || !!birthDayError.value;

  if (typeError.value || contactError.value || birthDayError.value || !type.value) {
    return;
  }

  const personal = isPersonal.value;

  emit('submit', {
    type: type.value,
    contact: contact.value.trim(),
    comment: comment.value.trim(),
    fio: personal ? fioLabel.value : '',
    position: personal ? position.value : null,
    birthDay: personal ? displayDateToSql(birthDay.value) : null,
    mask: contactMaskSource.value || null,
  });
}
</script>

<template>
  <ModalForm
    v-model:open="isOpen"
    width="xs"
    :confirm-on-dirty="false"
    :title
  >
    <template #body>
      <div class="contact-form">
        <UiField
          :id="fieldIds.type"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.type, type)"
        >
          <UiCombobox
            v-model="typeModel"
            :placeholder="PH.type"
            :options="getTypeOptions"
            :error="showErrors && !!typeError"
            :disabled="loading"
            no-clear
            with-custom-option
            inline-search
            update-after-load
            :search-max-length="50"
          />
        </UiField>

        <UiField
          :id="fieldIds.contact"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.contact, contact)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiInput
            :id="fieldIds.contact"
            :key="contactInputKey"
            v-model="contact"
            no-clear
            autocomplete="off"
            :placeholder="PH.contact"
            :mask="contactInputMask"
            :max-length="contactMaxLength"
            :error="showErrors && !!contactError"
            :disabled="loading"
            @update:model-value="onInput"
          />
        </UiField>

        <div class="personal-row">
          <span class="personal-label">{{ $t('counterparty.master.steps.contact.field.personal') }}</span>
          <UiToggle
            :id="fieldIds.personal"
            v-model="isPersonal"
            :disabled="loading"
          />
        </div>

        <div
          v-if="isPersonal"
          class="representative"
        >
          <div class="representative__header">
            <span class="representative__title">{{ $t(PH.representative) }}</span>
            <UiButton
              v-if="showRepresentativeClear"
              class="representative__clear"
              color="black"
              mode="plain"
              size="s"
              :disabled="loading"
              @click="clearRepresentative"
            >
              <CircleCloseIcon v-prefix />
              <span>{{ $t(PH.clear) }}</span>
            </UiButton>
          </div>

          <UiField
            :id="fieldIds.fio"
            stretch
            with-tooltip
            :tooltip="ValueUtil.placeholderTooltip(PH.fio, fioOption)"
          >
            <UiCombobox
              v-model="fioModel"
              :placeholder="PH.fio"
              :options="fioOptions"
              :disabled="loading"
              no-clear
              no-chevron
              with-custom-option
              :type-to-add-label="PH.typeToAdd"
              inline-search
              highlight-matches
              :search-max-length="COMMENT_MAX_LENGTH"
            />
          </UiField>

          <UiField
            :id="fieldIds.birthDay"
            stretch
            with-tooltip
            :tooltip="ValueUtil.placeholderTooltip(PH.birthDay, birthDay)"
            :tooltip-disabled="tooltipsDisabled"
            :error="showErrors ? birthDayError : undefined"
          >
            <div class="birth-day">
              <DateRangePicker
                ref="birthDayPicker"
                :date-range="birthDayRange"
                :single-date-picker="true"
                :ranges="false"
                :locale-data="localeData"
                :max-date="birthDayMax"
                :disabled="loading"
                opens="left"
                show-dropdowns
                auto-apply
                @update:model-value="onBirthDayPickerChange"
              >
                <template #input>
                  <UiInput
                    :id="fieldIds.birthDay"
                    v-model="birthDay"
                    no-clear
                    :placeholder="PH.birthDay"
                    :mask-placeholder="$t(PH.birthDayFormat)"
                    :mask="BIRTH_DAY_MASK"
                    inputmode="numeric"
                    :error="showErrors && !!birthDayError"
                    :disabled="loading"
                    @click.stop="onBirthDayClick"
                    @blur="onBirthDayBlur"
                    @update:model-value="onInput"
                  />
                </template>
              </DateRangePicker>
            </div>
          </UiField>

          <UiField
            :id="fieldIds.position"
            stretch
            with-tooltip
            :tooltip="ValueUtil.placeholderTooltip(PH.position, position)"
          >
            <UiCombobox
              v-model="position"
              :placeholder="PH.position"
              :options="getPositionOptions"
              :disabled="loading"
              no-clear
              with-custom-option
              :type-to-add-label="PH.typeToAdd"
              inline-search
              update-after-load
              highlight-matches
              :search-max-length="50"
            />
          </UiField>
        </div>

        <UiField
          :id="fieldIds.comment"
          stretch
          with-tooltip
          :tooltip="ValueUtil.placeholderTooltip(PH.comment, comment)"
          :tooltip-disabled="tooltipsDisabled"
        >
          <UiTextarea
            :id="fieldIds.comment"
            v-model="comment"
            :placeholder="PH.comment"
            :max-length="COMMENT_MAX_LENGTH"
            :rows="3"
            :disabled="loading"
            @update:model-value="onInput"
          />
        </UiField>
      </div>
    </template>

    <template #footer>
      <CloseButton
        :disabled="loading"
        @click="isOpen = false"
      />

      <SaveButton
        v-if="isEdit"
        :loading="loading"
        @click="submit"
      />
      <AddButton
        v-else
        :loading="loading"
        @click="submit"
      />
    </template>
  </ModalForm>
</template>

<style scoped lang="scss">
.contact-form {
  display: flex;
  padding: 8px 0;
  flex-direction: column;
  gap: 12px;
}

.personal-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  height: 36px;
  padding: 0 12px;
  border-radius: 8px;
  background-color: var(--color-gray-075);
}

.personal-label {
  color: var(--color-gray-1000);
  font-size: var(--font-size-l);
  line-height: var(--line-height-l);
}

.representative {
  display: flex;
  padding: 12px;
  flex-direction: column;
  gap: 12px;
  border: var(--border-gray-200);
  border-radius: 8px;
}

.representative__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
}

.representative__title {
  color: var(--color-gray-900);
  font-size: var(--font-size-l);
  font-weight: var(--font-weight-bold);
  line-height: var(--line-height-l);
}

.representative__clear {
  flex-shrink: 0;
  margin: -4px -8px -4px 0;
}

.birth-day {
  width: 100%;
  min-width: 0;

  :deep(.vue-daterange-picker) {
    display: block !important;
    width: 100%;
    min-width: 0 !important;
  }

  :deep(.vue-daterange-picker .reportrange-text) {
    display: block;
    width: 100%;
    overflow: visible !important;
  }
}
</style>
